import { useLocation } from 'react-router';
import agenticSectionPath from 'utils/agenticSectionPath';

/**
 * URL schema for the agents/chatflow settings tree:
 *
 *   /{section}                                     -> agents list (or empty state)
 *   /{section}/:flowId                             -> workspace landing for the flow
 *   /{section}/:flowId/instructions                -> instruction editor (default kind)
 *   /{section}/:flowId/instructions/:kind          -> base | tags | summary
 *   /{section}/:flowId/skills                      -> skills panel
 *   /{section}/:flowId/skills/:skillId             -> inline skill editor
 *   /{section}/:flowId/tools                       -> tools panel
 *   /{section}/:flowId/tools/:toolId               -> inline tool editor
 *   /{section}/:flowId/knowledge                   -> knowledge panel
 *   /{section}/:flowId/knowledge/:knowledgeId      -> inline knowledge editor
 *   /{section}/:flowId/tags                        -> tags panel
 *   /{section}/:flowId/tags/:tagId               -> inline tag editor
 *   /{section}/:flowId/metrics                     -> metrics panel
 *   /{section}/:flowId/metrics/:metricId           -> inline metric editor
 *   /{section}/:flowId/history                     -> history
 *   /{section}/:flowId/overview                    -> agent settings (General + Configuration tabs)
 *   /{section}/:flowId/pages                       -> pages canvas
 *   /{section}/:flowId/pages/:pageId               -> pages canvas focused on a page
 *   /{section}/:flowId/entity-types                -> pages library
 *   /{section}/:flowId/entity-types/:entityTypeId  -> entity type editor
 *   /{section}/:flowId/intents                     -> pages library
 *   /{section}/:flowId/intents/:intentId           -> intent editor
 *   /{section}/:flowId/integrations                -> pages library
 *   /{section}/:flowId/integrations/:integrationId -> integration editor
 *
 * `{section}` is either `agents` (agenticChatFlow flag on) or `chatFlow` (off).
 *
 * Sections recognised by the agent builder shell (UI). All but `pages` are agentic-only.
 */
export const AGENTIC_SECTIONS = new Set([
  'instructions',
  'skills',
  'tools',
  'knowledge',
  'tags',
  'metrics',
  'history',
  'overview',
]);

export const PAGES_SECTIONS = new Set(['pages', 'entity-types', 'intents', 'integrations']);

const INSTRUCTION_KINDS = new Set(['base', 'tags', 'summary']);

/**
 * Strip the canonical section prefix from a pathname and return the remaining
 * segments. Returns an empty array when the path is exactly the section root.
 */
const stripPrefix = (pathname, authCompany) => {
  const prefix = agenticSectionPath(authCompany);
  if (!pathname.startsWith(prefix)) return null;
  const rest = pathname.slice(prefix.length).replace(/^\/+/, '').replace(/\/+$/, '');
  return rest ? rest.split('/') : [];
};

/**
 * Parse the current location into a structured route object. Anything missing
 * is set to `null`. Unknown sections are dropped to `null` so the shell can
 * default to its preferred initial section.
 */
export const parseAgenticRoute = (pathname, authCompany) => {
  const segments = stripPrefix(pathname, authCompany);
  const out = {
    flowId: null,
    section: null,
    instructionKind: null,
    skillId: null,
    toolId: null,
    knowledgeId: null,
    tagId: null,
    metricId: null,
    pageId: null,
    entityTypeId: null,
    intentId: null,
    integrationId: null,
  };
  if (!segments) return out;
  if (segments.length === 0) return out;

  const [flowId, section, ...tail] = segments;
  out.flowId = flowId || null;
  if (!section) return out;

  if (AGENTIC_SECTIONS.has(section)) {
    out.section = section;
    if (section === 'instructions') {
      const kind = tail[0];
      out.instructionKind = INSTRUCTION_KINDS.has(kind) ? kind : 'base';
    } else if (section === 'skills') {
      out.skillId = tail[0] || null;
    } else if (section === 'tools') {
      out.toolId = tail[0] || null;
    } else if (section === 'knowledge') {
      out.knowledgeId = tail[0] || null;
    } else if (section === 'tags') {
      out.tagId = tail[0] || null;
    } else if (section === 'metrics') {
      out.metricId = tail[0] || null;
    }
    return out;
  }

  if (PAGES_SECTIONS.has(section)) {
    out.section = section;
    if (section === 'pages') out.pageId = tail[0] || null;
    if (section === 'entity-types') out.entityTypeId = tail[0] || null;
    if (section === 'intents') out.intentId = tail[0] || null;
    if (section === 'integrations') out.integrationId = tail[0] || null;
  }

  return out;
};

export const useAgenticRoute = authCompany => {
  const location = useLocation();
  return parseAgenticRoute(location.pathname, authCompany);
};

/**
 * Build an agentic URL. Pass partial `parts` to construct the canonical path
 * for a particular state. Unknown / missing fields are skipped.
 */
export const buildAgenticPath = (authCompany, parts = {}) => {
  const base = agenticSectionPath(authCompany);
  if (!parts.flowId) return base;
  const segs = [base, parts.flowId];
  const { section } = parts;
  if (!section) return segs.join('/');
  segs.push(section);

  if (section === 'instructions' && parts.instructionKind) {
    segs.push(parts.instructionKind);
  } else if (section === 'skills' && parts.skillId) {
    segs.push(parts.skillId);
  } else if (section === 'tools' && parts.toolId) {
    segs.push(parts.toolId);
  } else if (section === 'knowledge' && parts.knowledgeId) {
    segs.push(parts.knowledgeId);
  } else if (section === 'tags' && parts.tagId) {
    segs.push(parts.tagId);
  } else if (section === 'metrics' && parts.metricId) {
    segs.push(parts.metricId);
  } else if (section === 'pages' && parts.pageId) {
    segs.push(parts.pageId);
  } else if (section === 'entity-types' && parts.entityTypeId) {
    segs.push(parts.entityTypeId);
  } else if (section === 'intents' && parts.intentId) {
    segs.push(parts.intentId);
  } else if (section === 'integrations' && parts.integrationId) {
    segs.push(parts.integrationId);
  }
  return segs.join('/');
};

/**
 * Convert the URL state into a `context.current`-shaped value (the stack item
 * shape consumed by the existing ChatFlowContext consumers).
 */
export const routeToContextCurrent = route => {
  if (!route) return null;
  if (route.skillId) return { type: 'skill', skill: route.skillId };
  if (route.toolId) return { type: 'agent-tool', agentTool: route.toolId };
  if (route.knowledgeId) return { type: 'knowledge', knowledge: route.knowledgeId };
  if (route.tagId) return { type: 'tag', tag: route.tagId };
  if (route.metricId) return { type: 'metric', metric: route.metricId };
  if (route.pageId) return { type: 'page', page: route.pageId };
  if (route.entityTypeId) return { type: 'entity-type', entityType: route.entityTypeId };
  if (route.intentId) return { type: 'intent', intent: route.intentId };
  if (route.integrationId) return { type: 'integration', integration: route.integrationId };
  return null;
};

/**
 * Inverse of `routeToContextCurrent`: given a context entry, derive the URL
 * parts so a `context.set(...)` call can navigate.
 */
export const contextCurrentToRouteParts = (current, fallbackSection) => {
  if (!current) return { section: fallbackSection };
  switch (current.type) {
    case 'skill':
      return { section: 'skills', skillId: current.skill };
    case 'agent-tool':
      return { section: 'tools', toolId: current.agentTool };
    case 'knowledge':
      return { section: 'knowledge', knowledgeId: current.knowledge };
    case 'tag':
      return { section: 'tags', tagId: current.tag };
    case 'metric':
      return { section: 'metrics', metricId: current.metric };
    case 'page':
      return { section: 'pages', pageId: current.page };
    case 'entity-type':
      return { section: 'entity-types', entityTypeId: current.entityType };
    case 'intent':
      return { section: 'intents', intentId: current.intent };
    case 'integration':
      return {
        section: 'integrations',
        integrationId: current.integration,
      };
    default:
      return { section: fallbackSection };
  }
};
